import { useCallback, useMemo, useRef, useState } from 'react';
import { X, CheckCircle2, AlertTriangle, Info, XCircle } from 'lucide-react';
import { ContextoAvisos } from '../lib/contextoAvisos';

// Sistema de avisos: toast efímero + centro persistente.
//
// EL PROBLEMA QUE RESUELVE. Hasta aquí cada pantalla se las arreglaba
// sola: unas pintaban un recuadro rojo que se quedaba ahí, otras un texto
// verde que desaparecía al recargar, y las notificaciones del servidor
// —stock bajo, solicitudes pendientes— solo se veían entrando a su
// módulo. Un aviso que hay que ir a buscar no avisa.
//
// LA REGLA. Un toast es **efímero y no interrumpe**: nada de modales para
// decir "guardado". Pero lo que importa **no se pierde**: todo aviso que
// no sea un simple acuse queda en el centro de notificaciones hasta que
// alguien lo lea. Esa es la diferencia entre molestar y avisar.
const DURACION = { exito: 3500, info: 5000, aviso: 8000, error: 0 };
//                                                        ↑ el error NO se
// va solo: si algo falló, quien lo provocó tiene que cerrarlo a mano. Un
// error que desaparece a los tres segundos es un error que nadie leyó.

const ICONOS = {
  exito: CheckCircle2,
  info: Info,
  aviso: AlertTriangle,
  error: XCircle,
};

const ESTILOS = {
  exito: 'bg-white border-green-200 text-green-900',
  info: 'bg-white border-blue-200 text-blue-900',
  aviso: 'bg-white border-amber-200 text-amber-900',
  error: 'bg-white border-red-200 text-red-900',
};

const COLOR_ICONO = {
  exito: 'text-green-700',
  info: 'text-[var(--color-marca)]',
  aviso: 'text-amber-700',
  error: 'text-red-600',
};

export function ProveedorAvisos({ children }) {
  const [toasts, setToasts] = useState([]);
  const siguiente = useRef(1);

  const cerrar = useCallback((id) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const avisar = useCallback((mensaje, tipo = 'exito', opciones = {}) => {
    const id = siguiente.current++;
    setToasts((t) => [...t, { id, mensaje, tipo, ...opciones }]);
    const ms = opciones.duracion ?? DURACION[tipo] ?? 4000;
    if (ms > 0) setTimeout(() => cerrar(id), ms);
    return id;
  }, [cerrar]);

  const valor = useMemo(() => ({
    avisar,
    exito: (m, o) => avisar(m, 'exito', o),
    info: (m, o) => avisar(m, 'info', o),
    aviso: (m, o) => avisar(m, 'aviso', o),
    error: (m, o) => avisar(m, 'error', o),
  }), [avisar]);

  return (
    <ContextoAvisos.Provider value={valor}>
      {children}
      <Pila toasts={toasts} onCerrar={cerrar} />
    </ContextoAvisos.Provider>
  );
}

// Abajo a la derecha y por encima de todo, pero SIN capa que bloquee: se
// puede seguir trabajando con un toast en pantalla. Es la diferencia
// entre avisar e interrumpir.
function Pila({ toasts, onCerrar }) {
  if (!toasts.length) return null;
  return (
    <div className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2 w-80 max-w-[calc(100vw-2rem)]
                    pointer-events-none">
      {toasts.map((t) => {
        const Icono = ICONOS[t.tipo] || Info;
        return (
          <div
            key={t.id}
            role={t.tipo === 'error' ? 'alert' : 'status'}
            className={`pointer-events-auto flex items-start gap-2.5 border rounded-xl shadow-lg
                        px-3 py-2.5 text-sm ${ESTILOS[t.tipo] || ESTILOS.info}`}
          >
            <Icono className={`h-4 w-4 mt-0.5 shrink-0 ${COLOR_ICONO[t.tipo]}`} />
            <div className="min-w-0 flex-1">
              {t.titulo && <div className="font-semibold">{t.titulo}</div>}
              <div className="leading-snug break-words">{t.mensaje}</div>
            </div>
            <button
              onClick={() => onCerrar(t.id)}
              aria-label="Cerrar aviso"
              className="text-gray-500 hover:text-gray-700 shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
